import { TFunction } from 'i18next';
import { ObjectSchema } from 'yup';
import { dateSeperator } from '../../../../config';
import {
  entityLegalFormOptions,
  genderOptions,
  housingSituationOptions,
  maritalStatusOptions,
  professionOptions,
  salutationOptions,
  telephoneCountryCodesOptions,
} from '../../../../resources/base/models';
import { CountryCodesEnum, countryCodesOptionsValues } from '../../../../resources/base/models/country';
import { EnumCountryCode } from '../../../../resources/base/models/enums';
import { validateIBAN } from '../../../../services/api/iban/validIBAN';
import { dateFormat } from '../../../../services/common/format';
import { getOptionsListValues } from '../../../../services/common/miscUtils';
import { phoneValidationUtils, Yup } from '../../../../services/common/validation';
import { validateEmail, validateMobilePhone } from '../../../../services/common/validationUtils';
import { createAsyncValidator } from '../../utils/asyncValidator';
import { generateCompanyDataFormValidationSchema } from '../utils';
import {
  AdditionalInfoDataFormType,
  AddressDataFormType,
  CompanyDataFormType,
  ExecutiveInfoDataFormType,
  GeneralInfoDataFormType,
  PaymentDataFormType,
  PersonalInfoDataFormType,
  SignatoryInfoDataFormType,
} from './initialValues';

const regexNumeric = /^[0-9]+$/;
const regexName =
  /^[a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß'\\.-][a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß '\\.-]+$/;
const regexNumbersAndLetters =
  /^[-a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß\d\\.'][-a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß \\.0-9']*$/;
const regexBIC = /^[A-Za-z0-9]+$/;
const regexNameCompany =
  /^[a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß\-\\.!'@#$%^&*()_?0-9][a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠš ß\-\\.!'@#$%^&*()_?0-9]+$/;
const regexBankAccountOwner =
  /^[a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß\-\\.!'@#$%^&*()_?0-9][a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠš ß\-\\.!'@#$%^&*()_?0-9]+$/;

let _t = (() => '') as TFunction;
// eslint-disable-next-line prettier/prettier
const t =
  (...args: Parameters<TFunction>) =>
  () =>
    _t(...args);

const salutation = Yup.string().oneOf(getOptionsListValues(salutationOptions), t('validation:required'));
const gender = Yup.string()
  .required(t('validation:requiredSelect'))
  .oneOf(getOptionsListValues(genderOptions), t('validation:required'));
const firstName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const lastName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const birthName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const email = validateEmail();

const mobilePhone = validateMobilePhone();

const telephonePhone = Yup.string()
  .matches(phoneValidationUtils.landline.regexWithPrefix, t('validation:invalidCharacter'))
  .transform((value) => value.replace(/\s+/g, ''))
  .landlinePhoneLength();
const dateOfBirth = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:invalidDateformat'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'))
  .fullAge(18, dateSeperator, t('validation:fullAge'));
const profession = Yup.string().oneOf(getOptionsListValues(professionOptions), t('validation:required'));

const mobilePhoneCountryCode = Yup.string()
  .oneOf(getOptionsListValues(telephoneCountryCodesOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const birthCountry = Yup.string()
  .oneOf(countryCodesOptionsValues, t('validation:invalidData'))
  .required(t('validation:required'));

const zipCodeOfBirth = Yup.string()
  .min(5, t('validation:minLength', { value: 5 }))
  .max(5, t('validation:maxLength', { value: 5 }))
  .matches(regexNumeric, t('validation:invalidCharacter'));

const zipCodeOfBirthWithFranceCondition = zipCodeOfBirth.when('ceoBirthCountry', {
  is: (value) => value === 'FR' || value === EnumCountryCode.FRANCE,
  then: (schema) => schema.required(t('validation:required')),
});

const nationality = Yup.string()
  .oneOf(countryCodesOptionsValues, t('validation:invalidData'))
  .required(t('validation:required'));

const residenceStatus = Yup.string()
  .oneOf(getOptionsListValues(housingSituationOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const houseNumber = Yup.string()
  .required(t('validation:required'))
  .matches(regexNumbersAndLetters, t('validation:invalidCharacter'))
  .max(120, t('validation:maxLength', { value: 120 }));

const optionalLine = Yup.string()
  .matches(regexNumbersAndLetters, t('validation:invalidCharacter'))
  .max(120, t('validation:maxLength', { value: 120 }));

const zipCode = Yup.string()
  .matches(regexNumeric, t('validation:invalidCharacter'))
  .required(t('validation:required'))
  .length(5, t('validation:minLength', { value: 5 }));

const city = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value?) => !value || value === value.trim())
  .matches(regexName, t('validation:invalidCharacter'))
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .when('ceoBirthCountry', {
    is: (value) => value === 'FR' || value === EnumCountryCode.FRANCE,
    then: (schema) => schema.required(t('validation:required')),
  });

const countryCode = Yup.string().oneOf([CountryCodesEnum.FR]).required(t('validation:required'));

const maritalStatus = Yup.string()
  .oneOf(getOptionsListValues(maritalStatusOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const numberOfDependentChildren = Yup.number().required(t('validation:required'));

const netIncomeAmount = Yup.number().required(t('validation:required'));
const additionalIncomeAmount = Yup.number().required(t('validation:required'));
const monthlyRent = Yup.number().required(t('validation:required'));
const otherMonthlyCosts = Yup.number().required(t('validation:required'));

const bankAccountOwner = Yup.string()
  .matches(regexBankAccountOwner, t('validation:invalidCharacter'))
  .required(t('validation:required'))
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }));
const openingDate = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:required'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'));

const ibanValidator = createAsyncValidator(validateIBAN, { throttleMs: 500 });

const iban = Yup.string()
  .required(t('validation:required'))
  .min(10, t('validation:minLength', { value: 10 }))
  .max(40, t('validation:maxLength', { value: 40 }))
  .test('iban', t('validation:invalidCharacter'), async (value) => {
    if (!value || value.length < 10 || value.length > 40) {
      return true;
    }

    try {
      const result = await ibanValidator(value);
      return result.isValid;
    } catch (error) {
      return false;
    }
  });
const bic = Yup.string()
  .matches(regexBIC, t('validation:invalidCharacter'))
  .required(t('validation:required'))
  .min(8, t('validation:minLength', { value: 8 }))
  .max(11, t('validation:maxLength', { value: 11 }));

const legalEntity = Yup.string()
  .required(t('validation:required'))
  .oneOf(getOptionsListValues(entityLegalFormOptions), t('validation:required'));

const companyName = Yup.string()
  .required(t('validation:required'))
  .matches(regexNameCompany, t('validation:invalidCharacter'))
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }));

const apecode = Yup.string()
  .required(t('validation:required'))
  .matches(/^[0-9]{4}[A-Z]{1}$/, t('validation:invalidCharacter'));

const companyCreationDate = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:invalidDateformat'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'));

export const getGeneralInfoDataFormValidationSchema = (__t: TFunction): ObjectSchema<GeneralInfoDataFormType> => {
  _t = __t;

  return Yup.object({
    legalEntity,
    companyName,
    apecode,
    companyCreationDate,
    mobilePhoneCountryCode,
    mobilePhone,
    telephonePhone,
    email,
  });
};

export const getAddressDataFormValidationSchema = (__t: TFunction): ObjectSchema<AddressDataFormType> => {
  _t = __t;

  return Yup.object({
    addressLine1: houseNumber,
    optionalLine,
    zipCode,
    city,
    country: countryCode,
  });
};

export const getPersonalInfoDataFormValidationSchema = (__t: TFunction): ObjectSchema<PersonalInfoDataFormType> => {
  _t = __t;

  return Yup.object({
    gender,
    ceoFirstName: firstName,
    ceoLastName: lastName,
    ceoBirthName: birthName,
    ceoDateOfBirth: dateOfBirth,
    ceoNationality: nationality,
    ceoBirthCountry: birthCountry,
    ceoBirthPlaceZip: zipCodeOfBirthWithFranceCondition,
    ceoBirthPlaceCity: city,
  });
};

export const getExecutiveInfoDataFormValidationSchema = (__t: TFunction): ObjectSchema<ExecutiveInfoDataFormType> => {
  _t = __t;

  return Yup.object({
    salutation,
    gender,
    ceoFirstName: firstName,
    ceoLastName: lastName,
    ceoBirthName: birthName,
    ceoDateOfBirth: dateOfBirth,
    ceoNationality: nationality,
    ceoBirthCountry: birthCountry,
    ceoBirthPlaceZip: zipCodeOfBirth,
    ceoBirthPlaceCity: city,
    email,
    mobilePhone: validateMobilePhone(false),
    mobilePhoneCountryCode,
  });
};

export const getSignatoryInfoDataFormValidationSchema = (__t: TFunction): ObjectSchema<SignatoryInfoDataFormType> => {
  _t = __t;

  return Yup.object({
    salutation,
    gender,
    ceoFirstName: firstName,
    ceoLastName: lastName,
    ceoBirthName: birthName,
    ceoDateOfBirth: dateOfBirth,
    ceoNationality: nationality,
    ceoBirthCountry: birthCountry,
    ceoBirthPlaceZip: zipCodeOfBirth,
    ceoBirthPlaceCity: city,
    email,
    mobilePhone: validateMobilePhone(false),
    mobilePhoneCountryCode,
  });
};

export const getAdditionalInfoDataFormValidationSchema = (__t: TFunction): ObjectSchema<AdditionalInfoDataFormType> => {
  _t = __t;

  return Yup.object({
    professionEntrepreneur: profession,
    residenceStatus,
    maritalStatus,
    numberOfDependentChildren,
    netIncomeAmount,
    otherIncome: additionalIncomeAmount,
    monthlyPropertyCharge: monthlyRent,
    otherMonthlyCosts,
  });
};

export const getCompanyDataFormValidationSchema = (__t: TFunction): Yup.Lazy<ObjectSchema<CompanyDataFormType>> => {
  _t = __t;

  return Yup.lazy((values: CompanyDataFormType) => {
    return generateCompanyDataFormValidationSchema(_t, values) as any;
  });
};

export const getPaymentDataFormValidationSchema = (__t: TFunction): ObjectSchema<PaymentDataFormType> => {
  _t = __t;

  return Yup.object({
    bankAccountOwner,
    openingDate,
    iban,
    bic,
  });
};
